import type { BenefitDetailDto, ProvidedServiceDto } from "./benefit-types"
import { pseEnergyTypeLabels } from "./benefit-enums"

export function energyCheckboxState(value: boolean | null): boolean | "indeterminate" {
  return value === null ? "indeterminate" : value
}

export interface EnergyServiceOption {
  id: number
  serviceId: number
  providerId: number
  providerName: string
  serviceName: string
  isActive: boolean
  isPseEligible?: boolean
}

/** Supplementary costs may use any active heating service; preserve selected historical rows. */
export function energyServiceOptionDisabled(option: EnergyServiceOption, currentId: string): boolean {
  return currentId !== String(option.id) && !option.isActive
}

export interface EnergyServiceDraft {
  key: string
  id: string
  billingType: string
  useBackup: boolean
  surrogate: boolean
  pseEligible: boolean
  amounts: { billingPeriod: number; amount: string }[]
}

export interface EnergyIntakeDraft {
  pseEnabled: boolean
  sheapEnabled: boolean
  liheapEnabled: boolean
  services: EnergyServiceDraft[]
  pse: {
    dateMovedInResidence: string; energyType: string; liheapCost: string; applicationType: string
    receivedEapThisYear: boolean | null; advancedMeteringInfrastructureOptOut: boolean | null
    useBackup: boolean | null; surrogate: boolean | null
  }
  sheap: {
    heatSource: string; numberOfBedrooms: string; conservationEducation: boolean | null
    previousAssistance: boolean | null; previousAssistanceAmount: string; heatWithRent: boolean | null
    useBackup: boolean | null; surrogate: boolean | null
  }
  liheap: {
    primaryServiceProviderServiceId: string; secondaryServiceProviderServiceId: string
    numberOfBedrooms: string; conservationEducation: boolean | null; heatWithRent: boolean | null
    previousAssistance: boolean | null; previousAssistanceAmount: string
    receivedEnergyAssistanceProgram: boolean | null; receivedEmergencyEnergyAssistanceProgram: boolean | null
    oes: boolean | null; oesTotal: string; useBackup: boolean | null; surrogate: boolean | null
  }
}

/** Legacy value wrappers and byte-converted enums coexist on the current API contract. */
export function energyCode(value: number | string | { value: number } | null | undefined): string {
  if (typeof value === "string") {
    // PseEnergyTypeJsonConverter emits its display label, unlike the SHEAP value wrapper.
    // Keep an unrecognized value visible and reject the save rather than converting NaN to null.
    return Object.entries(pseEnergyTypeLabels).find(([, label]) => label === value)?.[0] ?? value
  }
  const code = typeof value === "object" && value !== null ? value.value : value
  return code == null ? "" : String(code)
}

let nextService = 0
export function newEnergyService(): EnergyServiceDraft {
  return { key: `energy-${++nextService}`, id: "", billingType: "1", useBackup: false, surrogate: false, pseEligible: false,
    amounts: [{ billingPeriod: 0, amount: "" }] }
}

export function serviceBillingAmounts(current: EnergyServiceDraft["amounts"], billingType: number) {
  const old = new Map(current.map(a => [a.billingPeriod, a.amount]))
  return Array.from({ length: billingType }, (_, i) => {
    const billingPeriod = billingType === 1 ? 0 : i + 1
    return { billingPeriod, amount: old.get(billingPeriod) ?? "" }
  })
}

export function energyIntakeDraft(benefit: BenefitDetailDto, options: EnergyServiceOption[], requestedProgramIds: readonly number[] = []): EnergyIntakeDraft {
  const requested = new Set(requestedProgramIds)
  const p = benefit.pseBenefitDetails
  const s = benefit.sheapBenefitDetails
  const l = benefit.liheapBenefitDetails
  const dependencyLiheap = requested.has(5) || requested.has(9) || requested.has(11)
  let saved: any = null
  try { saved = benefit.energyIntakeDraftJson ? JSON.parse(benefit.energyIntakeDraftJson) : null } catch { saved = null }
  const savedBlock = (name: string, fallback: any) => saved?.[name] && typeof saved[name] === "object" ? { ...fallback, ...saved[name] } : fallback
  const savedValue = (block: any, name: string, fallback: any) => Object.prototype.hasOwnProperty.call(block, name) ? block[name] : fallback
  const savedServices = Array.isArray(saved?.services) ? saved.services : null
  const serviceId = (service: ProvidedServiceDto | null | undefined) =>
    service ? String(options.find(o => o.serviceId === service.service.Id && o.providerId === service.provider)?.id ?? "unresolved") : ""
  const services = (savedServices ?? benefit.servicesReceived.filter(s => s.service.CategoryId === 100).map(service => ({
    ...newEnergyService(),
    id: String(options.find(o => o.serviceId === service.service.Id && o.providerId === service.provider)?.id ?? ""),
    pseEligible: options.some(o => o.serviceId === service.service.Id && o.providerId === service.provider && o.isPseEligible),
    billingType: String(service.billingType), useBackup: service.useBackupServiceCost, surrogate: service.surrogateData,
    amounts: service.amounts.map(a => ({ billingPeriod: a.billingPeriod, amount: String(a.serviceCostAmount) })),
  }))).map((service: any, index: number) => ({
    ...newEnergyService(), key: service.key ?? `saved-${service.serviceProviderServiceId ?? service.id ?? index}`,
    id: service.serviceProviderServiceId == null ? String(service.id ?? "") : String(service.serviceProviderServiceId),
    pseEligible: Boolean(service.pseEligible) || options.some(o => o.id === Number(service.serviceProviderServiceId ?? service.id) && o.isPseEligible),
    billingType: service.billingType == null ? "" : String(service.billingType),
    useBackup: service.useBackup ?? false, surrogate: service.surrogate ?? false,
    amounts: Array.isArray(service.amounts) ? service.amounts.map((a: any) => ({ billingPeriod: Number(a.billingPeriod), amount: a.amount == null ? "" : String(a.amount) })) : [],
  }))
  const liheapPrimary = savedValue(savedBlock("liheap", {}), "primaryServiceProviderServiceId", serviceId(l?.primaryHeatingService ?? null))
  return {
    pseEnabled: saved?.pse != null || p != null || requested.has(0),
    sheapEnabled: saved?.sheap != null || s != null || requested.has(3),
    liheapEnabled: saved?.liheap != null || l != null || requested.has(1) || dependencyLiheap,
    services,
    pse: (() => { const block = savedBlock("pse", {})
      const onlineIntake = benefit.intakeSelection?.kind === "pse-online" || benefit.intakeType?.trim().toLowerCase() === "pse online"
      const applicationType = savedValue(block, "applicationType", p?.pseApplicationType == null
        ? (p == null && requested.has(0) && !onlineIntake ? "0" : "")
        : p.pseApplicationType)
      const liheapCostFallback = p?.liheapCost == null
        ? (p == null && requested.has(0) ? "0" : "")
        : String(p.liheapCost)
      return {
        dateMovedInResidence: savedValue(block, "dateMovedInResidence", p?.dateMovedInResidence?.slice(0, 10) ?? "") || "",
        energyType: savedValue(block, "energyType", energyCode(p?.energyType)) == null ? "" : energyCode(savedValue(block, "energyType", energyCode(p?.energyType))),
        // New PSE intake has no LIHEAP cost evidence yet, so the unused deduction starts at 0.
        // Existing rows and explicit saved draft values remain untouched.
        liheapCost: savedValue(block, "liheapCost", liheapCostFallback) == null ? "" : String(savedValue(block, "liheapCost", liheapCostFallback)),
        // A new manual PSE section starts as Regular. Existing PSE details (including an
        // explicit unknown) and imported SDH classifications remain untouched.
        applicationType: applicationType == null ? "" : String(applicationType),
        receivedEapThisYear: savedValue(block, "receivedEapThisYear", p?.recvdEAPThisYear ?? null) ?? null,
        advancedMeteringInfrastructureOptOut: savedValue(block, "advancedMeteringInfrastructureOptOut", p?.advancedMeteringInfrastructureOptOut ?? null) ?? null,
        useBackup: savedValue(block, "useBackup", p?.usedBackup ?? null) ?? null,
        surrogate: usesPseSurrogateUsage(services),
      }
    })(),
    liheap: (() => { const block = savedBlock("liheap", {})
      return {
        primaryServiceProviderServiceId: savedValue(block, "primaryServiceProviderServiceId", serviceId(l?.primaryHeatingService ?? null)) == null ? "" : String(savedValue(block, "primaryServiceProviderServiceId", serviceId(l?.primaryHeatingService ?? null))),
        secondaryServiceProviderServiceId: savedValue(block, "secondaryServiceProviderServiceId", serviceId(l?.secondaryHeatingService ?? null)) == null ? "" : String(savedValue(block, "secondaryServiceProviderServiceId", serviceId(l?.secondaryHeatingService ?? null))),
        numberOfBedrooms: savedValue(block, "numberOfBedrooms", l?.numberOfBedrooms == null ? "" : String(l.numberOfBedrooms)) == null ? "" : String(savedValue(block, "numberOfBedrooms", l?.numberOfBedrooms == null ? "" : String(l.numberOfBedrooms))), conservationEducation: savedValue(block, "conservationEducation", l?.conservationEducation ?? null) ?? null,
        heatWithRent: savedValue(block, "heatWithRent", l?.heatWithRent ?? null) ?? null, previousAssistance: savedValue(block, "previousAssistance", l?.previousAssistance ?? null) ?? null,
        previousAssistanceAmount: savedValue(block, "previousAssistanceAmount", l?.previousAssistanceAmount == null ? "" : String(l.previousAssistanceAmount)) == null ? "" : String(savedValue(block, "previousAssistanceAmount", l?.previousAssistanceAmount == null ? "" : String(l.previousAssistanceAmount))),
        receivedEnergyAssistanceProgram: savedValue(block, "receivedEnergyAssistanceProgram", l?.receivedEnergyAssistanceProgram ?? null) ?? null,
        receivedEmergencyEnergyAssistanceProgram: savedValue(block, "receivedEmergencyEnergyAssistanceProgram", l?.receivedEmergencyEnergyAssistanceProgram ?? null) ?? null,
        oes: savedValue(block, "oes", l?.oes ?? null) ?? null, oesTotal: savedValue(block, "oesTotal", l?.oesTotal == null ? "" : String(l.oesTotal)) == null ? "" : String(savedValue(block, "oesTotal", l?.oesTotal == null ? "" : String(l.oesTotal))), useBackup: savedValue(block, "useBackup", l?.usedBackup ?? null) ?? null,
        surrogate: usesLiheapSurrogateUsage(services, liheapPrimary),
      }
    })(),
    sheap: (() => { const block = savedBlock("sheap", {})
      return {
        heatSource: savedValue(block, "heatSource", energyCode(s?.heatSource)) == null ? "" : energyCode(savedValue(block, "heatSource", energyCode(s?.heatSource))), numberOfBedrooms: savedValue(block, "numberOfBedrooms", s?.numberOfBedrooms == null ? "" : String(s.numberOfBedrooms)) == null ? "" : String(savedValue(block, "numberOfBedrooms", s?.numberOfBedrooms == null ? "" : String(s.numberOfBedrooms))),
        conservationEducation: savedValue(block, "conservationEducation", s?.conservationEducation ?? null) ?? null, previousAssistance: savedValue(block, "previousAssistance", s?.previousAssistance ?? null) ?? null,
        previousAssistanceAmount: savedValue(block, "previousAssistanceAmount", s?.previousAssistanceAmount == null ? "" : String(s.previousAssistanceAmount)) == null ? "" : String(savedValue(block, "previousAssistanceAmount", s?.previousAssistanceAmount == null ? "" : String(s.previousAssistanceAmount))), heatWithRent: savedValue(block, "heatWithRent", s?.heatWithRent ?? null) ?? null,
        useBackup: savedValue(block, "useBackup", s?.usedBackup ?? null) ?? null, surrogate: savedValue(block, "surrogate", s?.usedSurrogate ?? null) ?? null,
      }
    })(),
  }
}

function textValue(value: unknown): string {
  return value == null ? "" : String(value)
}

function dollars(value: unknown, label: string): number | null {
  const text = textValue(value).trim()
  if (!text) return null
  if (!/^\d+(\.\d{1,2})?$/.test(text)) throw new Error(`${label}: enter non-negative dollars and cents.`)
  const parsed = Number(text)
  if (!Number.isFinite(parsed) || parsed > 9999999.99) throw new Error(`${label}: amount is too large.`)
  return parsed
}

/** PSE HELP uses surrogate data when either canonical PSE electric or gas row is marked. */
export function usesPseSurrogateUsage(services: readonly Pick<EnergyServiceDraft, "surrogate" | "pseEligible">[]): boolean {
  return services.some(service => service.pseEligible && service.surrogate)
}

/** LIHEAP uses surrogate data only when its selected primary service row is marked. */
export function usesLiheapSurrogateUsage(services: readonly Pick<EnergyServiceDraft, "id" | "surrogate">[], primaryServiceProviderServiceId: unknown): boolean {
  const primary = textValue(primaryServiceProviderServiceId).trim()
  return Boolean(primary) && services.some(service => service.id === primary && service.surrogate)
}

export function energyIntakeRequest(draft: EnergyIntakeDraft, concurrencyToken: string | null, allowPartial = false) {
  if (!concurrencyToken) throw new Error("Reload and reopen energy intake before saving.")
  const ids = draft.services.map(s => textValue(s.id).trim() ? Number(s.id) : null)
  const answeredIds = ids.filter((id): id is number => id !== null)
  if ((!allowPartial && ids.some(id => id === null)) || answeredIds.some(id => !Number.isInteger(id) || id <= 0) || new Set(answeredIds).size !== answeredIds.length)
    throw new Error("Choose a different heating service for each row.")
  const services = draft.services.map((s, index) => {
    const billingType = textValue(s.billingType).trim() ? Number(s.billingType) : null
    const periods = s.amounts.map(a => a.billingPeriod)
    if (billingType !== null && (![1, 6, 12].includes(billingType) || periods.length !== billingType || new Set(periods).size !== periods.length ||
      periods.some(p => billingType === 1 ? p !== 0 : p < 1 || p > billingType)))
      throw new Error(`Service ${index + 1}: enter all of the selected billing periods.`)
    const amounts = s.amounts.map(a => ({ billingPeriod: a.billingPeriod, amount: dollars(a.amount, `Service ${index + 1}`) }))
    if (!allowPartial && amounts.some(a => a.amount === null)) throw new Error(`Service ${index + 1}: enter every amount.`)
    return { serviceProviderServiceId: ids[index] ?? null, billingType, useBackup: s.useBackup, surrogate: s.surrogate, amounts }
  })
  const pseEnergyType = textValue(draft.pse.energyType).trim()
  const sheapHeatSource = textValue(draft.sheap.heatSource).trim()
  if (draft.pseEnabled && pseEnergyType !== "" && !["0", "1", "2", "3"].includes(pseEnergyType))
    throw new Error("Choose a recognized PSE energy type.")
  if (draft.sheapEnabled && (!allowPartial && !sheapHeatSource || sheapHeatSource && !["10", "11", "12", "13", "14", "15"].includes(sheapHeatSource))) throw new Error("Choose the SHEAP primary heating fuel.")
  if (!allowPartial && draft.pseEnabled && draft.pse.useBackup && !pseEnergyType) throw new Error("Choose a PSE energy type for backup costs.")
  const bedrooms = draft.sheap.numberOfBedrooms.trim() ? Number(draft.sheap.numberOfBedrooms) : null
  if (draft.sheapEnabled && ((!allowPartial && bedrooms === null) || bedrooms !== null && (!Number.isInteger(bedrooms) || bedrooms < 0 || bedrooms > 50)))
    throw new Error("Bedrooms must be a whole number from 0 to 50.")
  const liheapBedrooms = draft.liheap.numberOfBedrooms.trim() ? Number(draft.liheap.numberOfBedrooms) : null
  if (draft.liheapEnabled) {
    const primary = draft.liheap.primaryServiceProviderServiceId
    const secondary = draft.liheap.secondaryServiceProviderServiceId
    if ((primary && !ids.includes(Number(primary))) || (secondary && !ids.includes(Number(secondary))))
      throw new Error("Record costs for the selected LIHEAP heating services below.")
    if (secondary && (!primary || primary === secondary)) throw new Error("LIHEAP primary and secondary services must be different.")
    if ((!allowPartial && primary && liheapBedrooms === null) || liheapBedrooms !== null && (!Number.isInteger(liheapBedrooms) || liheapBedrooms < 0 || liheapBedrooms > 50))
      throw new Error("LIHEAP bedrooms must be a whole number from 0 to 50.")
  }
  return {
    concurrencyToken, services,
    liheap: draft.liheapEnabled ? {
      ...draft.liheap, surrogate: usesLiheapSurrogateUsage(draft.services, draft.liheap.primaryServiceProviderServiceId),
      primaryServiceProviderServiceId: draft.liheap.primaryServiceProviderServiceId ? Number(draft.liheap.primaryServiceProviderServiceId) : null,
      secondaryServiceProviderServiceId: draft.liheap.secondaryServiceProviderServiceId ? Number(draft.liheap.secondaryServiceProviderServiceId) : null,
      numberOfBedrooms: liheapBedrooms,
      previousAssistanceAmount: dollars(draft.liheap.previousAssistanceAmount, "LIHEAP previous assistance"),
      oesTotal: dollars(draft.liheap.oesTotal, "Other energy services"),
    } : null,
    pse: draft.pseEnabled ? {
      ...draft.pse, surrogate: usesPseSurrogateUsage(draft.services), dateMovedInResidence: draft.pse.dateMovedInResidence || null,
      energyType: pseEnergyType === "" ? null : Number(pseEnergyType),
      applicationType: draft.pse.applicationType === "" ? null : Number(draft.pse.applicationType),
      liheapCost: dollars(draft.pse.liheapCost, "LIHEAP cost"),
      serviceProviderServiceIds: draft.services.filter(service => textValue(service.id).trim() && service.pseEligible).map(service => Number(service.id)),
    } : null,
    sheap: draft.sheapEnabled ? {
      ...draft.sheap, heatSource: sheapHeatSource ? Number(sheapHeatSource) : null, numberOfBedrooms: bedrooms,
      previousAssistanceAmount: dollars(draft.sheap.previousAssistanceAmount, "Previous assistance"),
    } : null,
  }
}
